import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Crown, Loader2, CalendarClock, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatDate } from "@/components/shared";

const PLANS = [
  { id: "weekly", label: "Weekly", sub: "7 days" },
  { id: "monthly", label: "Monthly", sub: "30 days" },
  { id: "yearly", label: "Yearly", sub: "365 days" },
  { id: "custom", label: "Custom", sub: "Pick days" },
];

export function SubscriptionDialog({ open, onOpenChange, user, onDone }) {
  const [plan, setPlan] = useState(null);
  const [days, setDays] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setPlan(null);
      setDays("");
    }
  }, [open]);

  if (!user) return null;

  const uid = user.user_id;
  const customValid = plan === "custom" && Number(days) > 0;
  const canGrant = plan && (plan !== "custom" || customValid);

  const grant = async () => {
    setBusy(true);
    try {
      if (plan === "custom") {
        await api.premium(uid, true, { days: Number(days) });
        toast.success(`Premium granted · ${days} days`);
      } else {
        await api.premium(uid, true, { plan });
        toast.success(`Premium granted · ${plan}`);
      }
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.premium(uid, false);
      toast.success("Premium revoked");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="subscription-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-300" /> Manage subscription
          </DialogTitle>
          <DialogDescription>
            Grant or revoke premium for <span className="text-foreground font-medium">{user.name || uid}</span>.
            Premium auto-expires on its end date.
          </DialogDescription>
        </DialogHeader>

        {/* Current status */}
        <div className="rounded-lg bg-secondary/50 ring-1 ring-border/60 px-4 py-3 flex items-center gap-2.5 text-sm" data-testid="sub-current-status">
          <CalendarClock className="h-4 w-4 text-muted-foreground shrink-0" />
          {user.premium ? (
            <span>
              Currently <span className="text-amber-300 font-semibold">Premium</span>
              {user.premium_plan ? ` (${user.premium_plan})` : ""}
              {user.premium_expires_at ? ` — expires ${formatDate(user.premium_expires_at)}` : " — no expiry set"}
            </span>
          ) : (
            <span>Currently on the <span className="font-semibold">Free</span> plan</span>
          )}
        </div>

        {/* Plan picker */}
        <div className="space-y-2">
          <Label>Choose a plan</Label>
          <div className="grid grid-cols-2 gap-2" data-testid="sub-plan-options">
            {PLANS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPlan(p.id)}
                data-testid={`plan-${p.id}`}
                className={`text-left rounded-xl px-4 py-3 ring-1 transition-all duration-150 ${
                  plan === p.id
                    ? "ring-primary bg-primary/15"
                    : "ring-border/60 bg-secondary/30 hover:ring-primary/40"
                }`}
              >
                <div className="font-display text-base">{p.label}</div>
                <div className="text-xs text-muted-foreground">{p.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {plan === "custom" && (
          <div className="space-y-1.5">
            <Label htmlFor="custom-days">Number of days</Label>
            <Input
              id="custom-days"
              type="number"
              min={1}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              placeholder="e.g. 90"
              data-testid="sub-custom-days"
              autoFocus
            />
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          {user.premium ? (
            <Button variant="destructive" onClick={revoke} disabled={busy} data-testid="sub-revoke">
              <XCircle className="h-4 w-4" /> Revoke now
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={grant} disabled={busy || !canGrant} data-testid="sub-grant">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Crown className="h-4 w-4" /> Grant</>}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
